import { cva, type VariantProps } from "class-variance-authority";
import { twMerge } from "tailwind-merge";

const rootStyles = cva("flex items-start text-center", {
  variants: {
    size: {
      large: "gap-grid-component-spacing-4xs",
      small: "gap-grid-component-spacing-5xs",
    },
  },
  defaultVariants: { size: "large" },
});

const itemStyles = cva("flex flex-col items-center shrink-0", {
  variants: {
    size: {
      large: "gap-grid-component-spacing-2xs w-24",
      small: "gap-grid-component-spacing-4xs w-20",
    },
  },
  defaultVariants: { size: "large" },
});

const numberStyles = cva(
  "font-display font-light not-italic uppercase leading-none",
  {
    variants: {
      size: {
        large: "text-6xl",
        small: "text-4xl",
      },
    },
    defaultVariants: { size: "large" },
  },
);

const labelStyles = "font-body font-regular text-xs leading-normal";

type HeroCounterVariants = VariantProps<typeof rootStyles>;

export interface HeroCounterProps extends HeroCounterVariants {
  days: number | string;
  hours: number | string;
  mins: number | string;
  daysLabel?: string;
  hoursLabel?: string;
  minsLabel?: string;
  className?: string;
}

export const HeroCounter = ({
  days,
  hours,
  mins,
  daysLabel = "Days",
  hoursLabel = "Hours",
  minsLabel = "Mins",
  size = "large",
  className,
}: HeroCounterProps) => {
  return (
    <div className={twMerge(rootStyles({ size }), className)}>
      <Item size={size} value={days} label={daysLabel} />
      <Separator size={size} />
      <Item size={size} value={hours} label={hoursLabel} />
      <Separator size={size} />
      <Item size={size} value={mins} label={minsLabel} />
    </div>
  );
};

interface ItemProps {
  size: HeroCounterVariants["size"];
  value: number | string;
  label: string;
}

const Item = ({ size, value, label }: ItemProps) => (
  <div className={itemStyles({ size })}>
    <span className={twMerge(numberStyles({ size }), "w-full")}>
      {formatValue(value)}
    </span>
    <span className={twMerge(labelStyles, "w-full")}>{label}</span>
  </div>
);

const Separator = ({ size }: { size: HeroCounterVariants["size"] }) => (
  <div aria-hidden className={twMerge(itemStyles({ size }), "w-auto")}>
    <span className={twMerge(numberStyles({ size }), "whitespace-nowrap")}>
      :
    </span>
    <span className={twMerge(labelStyles, "invisible")}>&nbsp;</span>
  </div>
);

const formatValue = (value: number | string) =>
  typeof value === "number" ? value.toString().padStart(2, "0") : value;
